import {
  forwardRef,
  useId,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "./utils/cn";
import Label from "./Label";
import FieldError from "./FieldError";

type NativeProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange"
>;

export interface TextareaProps extends NativeProps {
  value?: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  containerClassName?: string;
}

/** Canonical multi-line text field. Supersedes common/TextAreaInput. */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      value,
      onChange,
      label,
      required,
      disabled,
      error,
      rows = 4,
      id,
      className,
      containerClassName,
      ...rest
    },
    ref
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div>
        {label && (
          <Label htmlFor={inputId} required={required} readOnly={disabled}>
            {label}
          </Label>
        )}
        <div
          className={cn(
            "transition rounded-control border bg-surface px-3 py-2",
            error
              ? "border-danger focus-within:border-danger"
              : "border-border-subtle focus-within:border-secondary",
            "focus-within:ring-2 focus-within:ring-secondary/15 focus-within:ring-offset-0",
            disabled && "opacity-60",
            containerClassName
          )}
        >
          <textarea
            ref={ref}
            id={inputId}
            rows={rows}
            value={value ?? ""}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            onChange={(event) => onChange(event.target.value)}
            className={cn(
              "w-full resize-y bg-transparent text-start text-menu placeholder:text-menu/35 focus:outline-none disabled:cursor-not-allowed",
              className
            )}
            {...rest}
          />
        </div>
        <FieldError id={errorId}>{error}</FieldError>
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
